import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** Default query key for calendar studio deep links, e.g. `/calendar?studio=<studioId>`. */
export const CALENDAR_STUDIO_QUERY_PARAM = 'studio';

function buildLocation(pathname, params, hash) {
  const qs = params.toString();
  return {
    pathname,
    search: qs ? `?${qs}` : '',
    hash: hash || '',
  };
}

function parseIdsKey(studioIdsKey) {
  if (!studioIdsKey) return [];
  return studioIdsKey.split(',').filter(Boolean);
}

/**
 * Keeps `selectedStudioId` in sync with a search param (Airbnb-style `?key=value`).
 * Uses `studioId` from each entry in `studios`.
 *
 * @param {Object} options
 * @param {string} [options.paramName='studio']
 * @param {{ studioId?: string }[] | undefined | null} options.studios
 * @param {boolean} [options.clearParamWhenDefault=true] — drop the param when the first studio is selected
 */
export function useSearchParamStudioId({
  paramName = CALENDAR_STUDIO_QUERY_PARAM,
  studios,
  clearParamWhenDefault = true,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const studioIdsKey = useMemo(
    () => (studios ?? []).map((s) => s?.studioId).filter(Boolean).join(','),
    [studios]
  );

  const defaultId = useMemo(() => parseIdsKey(studioIdsKey)[0], [studioIdsKey]);

  const [selectedStudioId, setSelectedStudioIdState] = useState(undefined);

  useEffect(() => {
    const studioIds = parseIdsKey(studioIdsKey);
    if (!studioIds.length) {
      setSelectedStudioIdState(undefined);
      return;
    }

    const params = new URLSearchParams(location.search);
    const raw = params.get(paramName);

    let effectiveId = defaultId;
    let invalidRaw = false;

    if (raw) {
      if (studioIds.includes(raw)) {
        effectiveId = raw;
      } else {
        invalidRaw = true;
        effectiveId = defaultId;
      }
    }

    setSelectedStudioIdState((prev) => (prev === effectiveId ? prev : effectiveId));

    const nextParams = new URLSearchParams(location.search);
    const wantParam = Boolean(
      effectiveId && (!clearParamWhenDefault || effectiveId !== defaultId)
    );

    if (invalidRaw) {
      nextParams.delete(paramName);
      if (wantParam) {
        nextParams.set(paramName, effectiveId);
      }
      navigate(buildLocation(location.pathname, nextParams, location.hash), { replace: true });
      return;
    }

    if (wantParam && nextParams.get(paramName) !== effectiveId) {
      nextParams.set(paramName, effectiveId);
      navigate(buildLocation(location.pathname, nextParams, location.hash), { replace: true });
      return;
    }

    if (!wantParam && nextParams.has(paramName)) {
      nextParams.delete(paramName);
      navigate(buildLocation(location.pathname, nextParams, location.hash), { replace: true });
    }
  }, [
    location.search,
    location.pathname,
    location.hash,
    paramName,
    navigate,
    studioIdsKey,
    defaultId,
    clearParamWhenDefault,
  ]);

  const setSelectedStudioId = useCallback(
    (id) => {
      if (!id) return;
      setSelectedStudioIdState(id);
      const params = new URLSearchParams(location.search);
      if (clearParamWhenDefault && defaultId && id === defaultId) {
        params.delete(paramName);
      } else {
        params.set(paramName, id);
      }
      navigate(buildLocation(location.pathname, params, location.hash), { replace: false });
    },
    [
      location.pathname,
      location.search,
      location.hash,
      navigate,
      paramName,
      clearParamWhenDefault,
      defaultId,
    ]
  );

  return { selectedStudioId, setSelectedStudioId };
}

export default useSearchParamStudioId;
